import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  TextField,
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
  Paper,
  Autocomplete,
  Avatar,
  Chip,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
} from "@mui/material";
import {
  Add,
  Delete,
  CheckCircle,
  Search as SearchIcon,
  Print,
} from "@mui/icons-material";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import Swal from "sweetalert2";
import { useNavigate } from "react-router-dom";
import api from "../../api";

const ProductBilling = () => {
  const navigate = useNavigate();
  const schoolId = localStorage.getItem("school_id");
  const createdBy = localStorage.getItem("user_id");

  const [sessions, setSessions] = useState([]);
  const [filters, setFilters] = useState({
    session: null
  });

  const [classList, setClassList] = useState([]);
  const [sections, setSections] = useState([]);
  const [rolls, setRolls] = useState([]);
  const [students, setStudents] = useState([]);
  const [products, setProducts] = useState([]);
  const [billingItems, setBillingItems] = useState([]);
  const [discountType, setDiscountType] = useState("amount");

  const [selectedClass, setSelectedClass] = useState("");
  const [selectedSection, setSelectedSection] = useState("");
  const [selectedRoll, setSelectedRoll] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedStudent, setSelectedStudent] = useState("");
  const [selectedType, setSelectedType] = useState([]);

  const [discount, setDiscount] = useState(0);
  const [paymentMode, setPaymentMode] = useState("cash");
  const [remarks, setRemarks] = useState("");
  const [invoiceData, setInvoiceData] = useState(null);
  const [openModal, setOpenModal] = useState(false);
  const [loading, setLoading] = useState(false);

  // --------------------------
  //  AUTH CHECK
  // --------------------------
  useEffect(() => {
    const token = localStorage.getItem("token");
    if (!token) {
      navigate("/login");
      return;
    }
  }, [navigate]);

  // 🧩 Init
  useEffect(() => {
    fetchSessions();
  }, []);

  // 🧩 Fetch Sessions
  const fetchSessions = async () => {
    try {
      const res = await api.get("school/academic_year/list", {
        headers: { "X-School-Id": schoolId },
      });

      if (res.data?.status !== false) {
        const data = res.data.data || [];
        data.sort((a, b) => (a.status === "active" ? -1 : 1));
        setSessions(data);

        // Auto-select active session
        if (!filters.session && data.length) {
          const active = data.find((s) => s.status === "active");
          setFilters((p) => ({ ...p, session: active || data[0] }));
        }
      }
    } catch {
      Swal.fire("Error", "Failed to fetch sessions", "error");
    }
  };

  // ✅ Fetch Classes
  useEffect(() => {
    const fetchClasses = async () => {
      try {
        const res = await api.get("/get-classes");
        setClassList(res.data?.data || []);
      } catch {
        setClassList([]);
      }
    };
    fetchClasses();
  }, []);

  // ✅ Fetch Sections (Depends on class)
  useEffect(() => {
    if (!selectedClass) {
      setSections([]);
      setSelectedSection("");
      return;
    }
    const fetchSection = async () => {
      try {
        const res = await api.get(`/get-sections/${selectedClass}`);
        setSections(res.data?.data || []);
      } catch {
        setSections([]);
      }
    };
    fetchSection();
  }, [selectedClass]);

  // ✅ Fetch Rolls (Depends on section)
  useEffect(() => {
    if (!selectedSection) {
      setRolls([]);
      setSelectedRoll("");
      return;
    }
    const fetchRoll = async () => {
      try {
        const res = await api.get(`/get-rolls/${selectedSection}`);
        if (res.data && res.data.success) {
          // ✅ remove duplicate roll_no
          const uniqueRolls = Array.from(
            new Map(
              res.data.data.map((r) => [r.roll_no, r])
            ).values()
          );

          setRolls(uniqueRolls);
          setSelectedRoll("");
        } else {
          setRolls([]);
        }
      } catch {
        setRolls([]);
      }
    };
    fetchRoll();
  }, [selectedSection]);

  // ✅ Reset items when Product Type cleared
  useEffect(() => {
    if (selectedType.length === 0) {
      setBillingItems([]);
      setProducts([]);
    }
  }, [selectedType]);

  // ✅ Search Student
  const handleSearch = async () => {
    if (!selectedClass && !selectedSection && !selectedRoll && !searchTerm) {
      toast.warning("Please select at least one filter or enter a student code.");
      return;
    }

    try {
      const params = new URLSearchParams();
      params.append("school_id", schoolId);
      if (filters.session) params.append("academic_year_id", filters.session.id);
      if (selectedClass) params.append("class_id", selectedClass);
      if (selectedSection) params.append("section_id", selectedSection);
      if (selectedRoll) params.append("roll_no", selectedRoll);
      if (searchTerm) params.append("query", searchTerm);

      const res = await api.get(`/get_student?${params.toString()}`);
      if (res.data?.success && res.data?.data?.length > 0) {
        const studentData = res.data.data;
        setStudents(studentData);
        setSelectedStudent(studentData[0]?.id || "");
        toast.success("Student found successfully!");
      } else {
        setStudents([]);
        toast.error("No student found!");
      }
    } catch {
      toast.error("Error fetching student data!");
    }
  };

  // ✅ Fetch Multiple Product Types
  useEffect(() => {
    const fetchProductsByMultipleTypes = async () => {
      if (selectedType.length === 0 || !selectedClass) return;

      let allFetched = [];

      for (const type of selectedType) {
        try {
          let res;

          if (type === "Book") {
            // ✅ Books are class-specific
            res = await api.get(`/product/bundle/class_products/${selectedClass}`);
          } else {
            // ✅ Other product types
            res = await api.get(
              `/product/bundle/filter_variants?type=${type.toLowerCase()}`
            );
          }

          if (res.data?.status === "success" && Array.isArray(res.data?.data)) {
            const fetched = res.data.data;

            if (type === "Book") {
              // ✅ Books → add all items and variants
              allFetched = [...allFetched, ...fetched];
            } else {
              // ✅ For others: keep one row per product, but collect all variants
              const productGroups = {};

              fetched.forEach((item) => {
                if (!productGroups[item.product_id]) {
                  productGroups[item.product_id] = {
                    ...item,
                    variants: [],
                  };
                }

                productGroups[item.product_id].variants.push({
                  variant_id: item.variant_id,
                  variant_name: item.variant_name,
                  stock_qty: item.stock_qty,
                  sale_price: item.sale_price,
                });
              });

              // push one per product (not per variant)
              allFetched = [...allFetched, ...Object.values(productGroups)];
            }
          }
        } catch (err) {
          console.error(`Error fetching ${type}:`, err);
        }
      }

      // ✅ Remove already-added duplicates
      const uniqueItems = allFetched.filter(
        (item) =>
          !billingItems.some(
            (x) => x.product === item.product_id && x.variant === item.variant_id
          )
      );

      if (uniqueItems.length === 0) {
        toast.info("All selected product types already added.");
        return;
      }

      // ✅ Prepare mapped billing items
      const mapped = uniqueItems.map((d) => ({
        product: d.product_id,
        variant: d.variant_id,
        quantity: 1,
        price: d.sale_price,
        item_discount: 0,
        variants:
          d.variants && d.variants.length > 0
            ? d.variants // for Dress / others
            : allFetched
              .filter((v) => v.product_id === d.product_id)
              .map((v) => ({
                variant_id: v.variant_id,
                variant_name: v.variant_name,
                stock_qty: v.stock_qty,
                sale_price: v.sale_price,
              })), // for Book
      }));

      setBillingItems((prev) => [...prev, ...mapped]);
      setProducts(allFetched);
    };

    fetchProductsByMultipleTypes();
  }, [selectedType, selectedClass]);




  // ✅ Fetch Variants for Product
  const fetchVariantsForItem = async (index, productId) => {
    try {
      const res = await api.get(`/product/bundle/view/${productId}`);
      if (res.data?.status === "success" && res.data?.data?.variants) {
        const updated = [...billingItems];
        updated[index].variants = res.data.data.variants;
        setBillingItems(updated);
      }
    } catch {
      toast.error("Error fetching variants!");
    }
  };

  // ✅ Update Billing Item
  const updateItem = (index, field, value) => {
    let updated = [...billingItems];

    // ✅ When product is changed
    if (field === "product") {
      updated[index][field] = value;
      updated[index]["variant"] = "";
      updated[index]["price"] = 0;
      fetchVariantsForItem(index, value);
    }
    // ✅ When item discount is changed
    if (field === "item_discount") {
      const gross = updated[index].quantity * updated[index].price;
      updated[index].item_discount = Math.min(gross, Math.max(0, value));
    }

    // ✅ When variant is changed
    if (field === "variant") {
      const duplicate = billingItems.some(
        (item, i) =>
          i !== index &&
          item.variant === value &&
          item.product === updated[index].product
      );
      if (duplicate) {
        toast.error("This variant is already added for this product!");
        return;
      }

      const v = updated[index].variants?.find(
        (x) => x.variant_id === value
      );

      if (v) {
        updated[index]["variant"] = v.variant_id;
        updated[index]["price"] = v.sale_price || 0;
      } else {
        updated[index]["variant"] = "";
        updated[index]["price"] = 0;
      }
    }

    // ✅ When quantity is changed
    if (field === "quantity") {
      updated[index]["quantity"] = parseInt(value) || 1;
    }

    setBillingItems(updated);
  };

  const addItem = () => {
    setBillingItems((prev) => [
      ...prev,
      { product: "", variant: "", quantity: 1, price: 0, item_discount: 0, variants: [] },
    ]);
  };

  const removeItem = (index) => {
    setBillingItems(billingItems.filter((_, i) => i !== index));
  };

  // ✅ Totals
  // const totalAmount = billingItems.reduce(
  //   (sum, i) => sum + i.quantity * i.price,
  //   0
  // );

  const totalAmount = billingItems.reduce((sum, i) => {
    const gross = i.quantity * i.price;
    const net = gross - (i.item_discount || 0);
    return sum + Math.max(0, net);
  }, 0);

  const netAmount = Math.max(0, totalAmount - (parseFloat(discount) || 0));

  // ✅ Submit Invoice
  const handleSubmit = async () => {
    if (!selectedStudent) {
      toast.warning("Please select a student before billing!");
      return;
    }
    if (billingItems.length === 0) {
      toast.warning("Please add at least one product!");
      return;
    }

    setLoading(true);
    const payload = {
      school_id: schoolId,
      academic_year_id: filters.session?.id,
      student_id: selectedStudent,
      items: billingItems.map((i) => ({
        product_variant_id: i.variant,
        quantity: i.quantity,
        price: i.price,
        item_discount: i.item_discount || 0
      })),
      discount: parseFloat(discount) || 0,
      discount_type: discountType,
      payment_mode: paymentMode,
      payment_status: "paid",
      remarks,
      credit_employee: true,
      created_by: createdBy,
    };

    try {
      const res = await api.post("/student/product/billing/create", payload);
      if (res.data?.status === "success" && res.data?.data?.invoice_id) {
        toast.success("Invoice created successfully!");
        setInvoiceData(res.data.data);
        setOpenModal(true);

        // ✅ Auto Reset All Data
        setBillingItems([]);
        setSelectedType([]);
        setStudents([]);
        setSelectedStudent("");
        setDiscount(0);
        setRemarks("");
        setSelectedRoll("");
        setSelectedSection("");
        setSelectedClass("");
      } else toast.error("Failed to create invoice!");
    } catch {
      toast.error("Error creating invoice!");
    } finally {
      setLoading(false);
    }
  };

  // ✅ Print Invoice (same as before)
  const handleInvoicePrint = async (invoiceId) => {
    try {
      const res = await api.get(`/student/product/billing/invoice/${invoiceId}`);
      if (!res.data.status) {
        toast.error("Invoice not found!");
        return;
      }

      const data = res.data.data;
      const printWindow = window.open("", "_blank");

      const schoolLogo = data.school_logo
        ? `https://dgps.co.in/public/admin/assets/img/schools/logo/${data.school_logo}`
        : "https://dgps.co.in/public/admin/assets/img/logo.png";

      const getItemNetTotal = (item) =>
        parseFloat(item.net_subtotal)
          ? item.net_subtotal
          : item.total;

      printWindow.document.write(`
        <html>
        <head>
            <title>Product Invoice</title>
            <style>
            body { font-family: 'Poppins', sans-serif; margin: 0; padding: 20px; background: #f4f6f9; }
            .receipt-box {
                background: #fff; border: 2px dashed #ccc; border-radius: 12px; padding: 20px;
                margin: 20px auto; max-width: 900px; box-shadow: 0 4px 12px rgba(0,0,0,0.1);
            }
            .school-header { display: flex; align-items: center; justify-content: space-between; }
            .school-logo { width: 90px; height: 90px; object-fit: contain; }
            .school-text { flex-grow: 1; text-align: center; }
            .school-name { font-size: 22px; font-weight: bold; color: #002f6c; margin-bottom: 5px; }
            .school-contact { font-size: 13px; color: #555; }
            .invoice-title { background: #002f6c; color: #fff; text-align: center; padding: 10px; font-size: 18px; font-weight: 600; margin-top: 20px; border-radius: 8px; }
            .info-row { display: flex; justify-content: space-between; font-size: 14px; margin: 10px 0; }
            table { width: 100%; border-collapse: collapse; margin-top: 20px; }
            th, td { border: 1px solid #ccc; padding: 10px; font-size: 14px; text-align: left; }
            th { background: #e2eefd; color: #002f6c; }
            .amount-box {
                margin-top: 20px; text-align: right; font-weight: 600; font-size: 16px;
                background: #f0f6ff; padding: 10px; border-radius: 8px;
            }
            .footer { text-align: center; margin-top: 20px; font-size: 12px; color: #777; }
            @media print { body { background: white; padding: 0; } }
            </style>
        </head>
        <body>
            ${["Office Copy", "Student Copy"]
          .map(
            (copyType) => `
            <div class="receipt-box">
                <div class="school-header">
                <img src="${schoolLogo}" class="school-logo" alt="Logo"/>
                <div class="school-text">
                    <div class="school-name">${data.school_name}</div>
                    <div class="school-contact">
                    ${data.school_address}<br>
                    ${data.school_phone ? "Phone: " + data.school_phone + " | " : ""}${data.school_email || ""}
                    </div>
                </div>
                </div>

                <div class="invoice-title">Product Invoice</div>
                <div class="info-row"><strong>Invoice No:</strong> ${data.invoice_no}</div>

                <div class="info-row">
                <span><strong>Student:</strong> ${data.student_name} (${data.student_code})</span>
                <span><strong>Class:</strong> ${data.class_name} - ${data.section_name}</span>
                <span><strong>Roll:</strong> ${data.roll_no}</span>
                </div>

                <table>
                <thead>
                    <tr>
                    <th>Product</th>
                    <th>Variant</th>
                    <th>Qty</th>
                    <th>Price</th>
                    <th>Discount</th>
                    <th>Total</th>
                    </tr>
                </thead>
                <tbody>
                    ${data.items
                .map(
                  (item) => `
                    <tr>
                        <td>${item.product_name}</td>
                        <td>${item.variant_name}</td>
                        <td>${item.quantity}</td>
                        <td>₹ ${item.price}</td>
                        <td>₹ ${item.item_discount}</td>
                        <td>₹ ${getItemNetTotal(item)}</td>
                    </tr>`
                )
                .join("")}
                </tbody>
                </table>

                <div class="amount-box">
                <div>Total Amount: ₹${data.total_amount}</div>
                <div>Discount: ₹${data.discount}</div>
                <div>Net Payable: <strong>₹${data.net_amount}</strong></div>
                </div>

                <div class="info-row">
                <span><strong>Payment Mode:</strong> ${data.payment_mode.toUpperCase()}</span>
                <span><strong>Status:</strong> ${data.payment_status.toUpperCase()}</span>
                <span><strong>Created By:</strong> ${data.created_by}</span>
                </div>

                <div class="footer">
                This is a system-generated invoice — no signature required.
                <br><small>${copyType}</small>
                </div>
            </div>`
          )
          .join("")}
            <script>window.onload = () => window.print(); window.onafterprint = () => window.close();</script>
        </body>
        </html>`);
      printWindow.document.close();
    } catch (err) {
      toast.error("Error while printing invoice!");
    }
  };

  // ✅ Thermal Printer Invoice Layout (Compact Version)
  const handleThermalInvoicePrint = async (invoiceId) => {
    try {
      const res = await api.get(`/student/product/billing/invoice/${invoiceId}`);
      if (!res.data.status) {
        toast.error("Invoice not found!");
        return;
      }

      const data = res.data.data;
      const printWindow = window.open("", "_blank");

      const schoolLogo = data.school_logo_bk
        ? `https://dgps.co.in/public/admin/assets/img/schools/logo/${data.school_logo_bk}`
        : "https://dgps.co.in/public/admin/assets/img/logo.png";

      const getItemNetTotal = (item) =>
        parseFloat(item.net_subtotal)
          ? item.net_subtotal
          : item.total;

      // 🧾 Thermal Print Layout
      printWindow.document.write(`
      <html>
      <head>
        <title>Thermal Product Invoice</title>
        <style>
          @page {
            size: 80mm auto;
            margin: 2mm;
          }
          body {
            font-family: 'Segoe UI', sans-serif;
            width: 70mm;
            margin: 0 auto;
            padding: 2mm;
            background: #fff;
            color: #000;
            font-size: 11px;
            -webkit-print-color-adjust: exact !important;
            image-rendering: pixelated;
          }

          .center { text-align: center; }
          .right { text-align: right; }
          .bold { font-weight: bold; }
          .line { border-bottom: 1px dashed #000; margin: 3px 0; }
          .school-logo {
            width: 45px; height: 45px; display: block;
            margin: 0 auto 3px; object-fit: contain;
          }
          .school-name { font-size: 13px; font-weight: bold; text-transform: uppercase; }
          .contact { font-size: 10px; line-height: 1.3; margin-bottom: 5px; }

          .title-bar {
            background: #000; color: #fff; text-align: center;
            font-weight: bold; font-size: 11px; padding: 3px 0;
            border-radius: 2px; margin: 4px 0;
          }

          .info-row {
            display: flex;
            justify-content: space-between;
            font-size: 11px;
            margin: 2px 0;
            word-wrap: break-word;
            width: 100%;
          }

          table {
            width: 100%;
            border-collapse: collapse;
            margin-top: 2px;
            font-size: 11px;
          }
          th, td {
            padding: 2px 0;
            text-align: left;
            white-space: nowrap;
            overflow: hidden;
            text-overflow: ellipsis;
          }
          th {
            border-bottom: 1px solid #000;
            font-weight: bold;
          }
          td {
            border-bottom: 1px dotted #000;
          }

          .amount-box {
            margin-top: 4px;
            border-top: 1px dashed #000;
            padding-top: 3px;
            font-size: 11px;
          }

          .footer {
            text-align: center;
            font-size: 9.5px;
            margin-top: 6px;
            border-top: 1px dashed #000;
            padding-top: 3px;
            font-style: italic;
            line-height: 1.3;
          }

          @media print {
            body { -webkit-print-color-adjust: exact !important; }
          }
        </style>
      </head>
      <body>
        <div class="center">
          <img src="${schoolLogo}" class="school-logo" alt="Logo"/>
          <div class="school-name">${data.school_name}</div>
          <div class="contact">
            ${data.school_address ? `${data.school_address}<br/>` : ""}
            ${data.school_phone ? `Ph: ${data.school_phone}<br/>` : ""}
            ${data.school_email || ""}
          </div>
        </div>

        <div class="title-bar">STUDENT COPY - PRODUCT INVOICE</div>

        <div class="info-row"><span>Invoice No:</span><span>${data.invoice_no}</span></div>
        <div class="info-row"><span>Student:</span><span>${data.student_name}</span></div>
        <div class="info-row"><span>Class:</span><span>${data.class_name}-${data.section_name}</span></div>
        <div class="info-row"><span>Roll:</span><span>${data.roll_no}</span></div>
        <div class="line"></div>

        <table>
          <thead>
            <tr>
              <th>Product</th>
              <th>Qty</th>
              <th class="right">Amt</th>
              <th class="right">Disc</th>
              <th class="right">Total</th>
            </tr>
          </thead>
          <tbody>
            ${data.items.map(
        (item) => `
                <tr>
                  <td>${item.product_name}</td>
                  <td>${item.quantity}</td>
                  <td class="right">₹${item.price}</td>
                  <td class="right">₹${item.item_discount}</td>
                  <td class="right">₹${getItemNetTotal(item)}</td>
                </tr>
              `
      ).join("")}
          </tbody>
        </table>

        <div class="amount-box">
          <div class="info-row"><span>Total:</span><span>₹${data.total_amount}</span></div>
          <div class="info-row"><span>Discount:</span><span>₹${data.discount}</span></div>
          <div class="info-row bold"><span>Net Payable:</span><span>₹${data.net_amount}</span></div>
        </div>

        <div class="line"></div>
        <div class="info-row"><span>Payment:</span><span>${data.payment_mode.toUpperCase()}</span></div>
        <div class="info-row"><span>Status:</span><span>${data.payment_status.toUpperCase()}</span></div>
        <div class="info-row"><span>Created By:</span><span>${data.created_by}</span></div>

        <div class="footer">
          Thank you for your payment!<br/>
          This is a system-generated invoice.<br/>
          No signature required.
        </div>

        <script>
          window.onload = function() {
            window.print();
          };
          window.onafterprint = function() {
            window.close();
          };
        </script>
      </body>
      </html>
    `);

      printWindow.document.close();
    } catch (err) {
      toast.error("Error while printing thermal invoice!");
    }
  };
  return (
    <Box sx={{ background: "#f4f6f8", minHeight: "100vh", p: 2 }}>
      <ToastContainer position="top-right" autoClose={2000} theme="colored" />

      <Card sx={{ mx: "auto", borderRadius: 4, boxShadow: 6 }}>
        <CardContent>
          <Typography variant="h5" fontWeight={700} color="primary" gutterBottom textAlign="center">
            📦 Student Product Billing
          </Typography>

          {/* Filters */}
          <Paper sx={{ p: 2, borderRadius: 2, mb: 3 }}>
            <Grid container spacing={2}>
              <Grid item xs={3}>
                <Autocomplete
                  options={sessions}
                  getOptionLabel={(opt) =>
                    `${opt.session_name || ""}${opt.status ? ` (${opt.status})` : ""}`
                  }
                  value={filters.session}
                  onChange={(_, v) => setFilters((p) => ({ ...p, session: v }))}
                  renderInput={(params) => (
                    <TextField {...params} label="Select Session" fullWidth />
                  )}
                  clearOnEscape
                />
              </Grid>
              {/* Class */}
              <Grid item xs={3}>
                <Autocomplete
                  options={classList}
                  getOptionLabel={(o) => o.class_name}
                  value={classList.find((c) => c.id === selectedClass) || null}
                  onChange={(e, v) => setSelectedClass(v ? v.id : "")}
                  renderInput={(p) => <TextField {...p} label="Select Class" />}
                />
              </Grid>

              {/* Section */}
              <Grid item xs={3}>
                <Autocomplete
                  disabled={!selectedClass}
                  options={sections}
                  getOptionLabel={(o) => o.section_name}
                  value={sections.find((s) => s.id === selectedSection) || null}
                  onChange={(e, v) => setSelectedSection(v ? v.id : "")}
                  renderInput={(p) => <TextField {...p} label="Select Section" />}
                />
              </Grid>

              {/* Roll */}
              <Grid item xs={3}>
                <Autocomplete
                  disabled={!selectedSection}
                  options={rolls}
                  getOptionLabel={(o) => o.roll_no?.toString() || ""}
                  value={rolls.find((r) => r.roll_no === selectedRoll) || null}
                  onChange={(e, v) => setSelectedRoll(v ? v.roll_no : "")}
                  renderInput={(p) => <TextField {...p} label="Select Roll" />}
                />
              </Grid>

              <Grid item xs={3}>
                <TextField
                  fullWidth
                  label="Search (Name / Mobile / Code)"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </Grid>

              <Grid item xs={3}>
                <Autocomplete
                  multiple
                  options={["Book", "Dress", "Shoes", "General"]}
                  value={selectedType}
                  onChange={(e, v) => setSelectedType(v)}
                  renderTags={(value, getTagProps) =>
                    value.map((option, index) => (
                      <Chip label={option} {...getTagProps({ index })} />
                    ))
                  }
                  renderInput={(params) => (
                    <TextField {...params} label="Product Type" placeholder="Select..." />
                  )}
                />
              </Grid>

              <Grid item xs={12} textAlign="center">
                <Button variant="contained" startIcon={<SearchIcon />} onClick={handleSearch}>
                  Search Student
                </Button>
              </Grid>
            </Grid>
          </Paper>

          {/* Student Info */}
          {students.length > 0 && (
            <Card sx={{ mb: 3, p: 2, display: "flex", alignItems: "center", boxShadow: 3 }}>
              <Avatar src={students[0].file} sx={{ width: 70, height: 70, mr: 3 }} />
              <Box>
                <Typography variant="h6" fontWeight={600}>
                  {students[0].name} ({students[0].student_code})
                </Typography>
                <Typography color="textSecondary">
                  📱 {students[0].mobile} | 🎓 {students[0].class} - {students[0].section} | Roll:{" "}
                  {students[0].roll_numbers}
                </Typography>
              </Box>
            </Card>
          )}



          {/* Billing Items */}
          {billingItems.length > 0 && (
            <>
              <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 1 }}>
                Product Items
              </Typography>
              {billingItems.map((item, i) => (
                <Grid container spacing={2} key={i} sx={{ mb: 1 }}>
                  <Grid item xs={2}>
                    <Autocomplete
                      options={products}
                      getOptionLabel={(o) => o.product_name || ""}
                      value={products.find((p) => p.product_id === item.product) || null}
                      onChange={(e, v) => updateItem(i, "product", v ? v.product_id : "")}
                      renderInput={(p) => <TextField {...p} label="Product" />}
                    />
                  </Grid>
                  <Grid item xs={3}>
                    <Autocomplete
                      options={item.variants || []}
                      getOptionLabel={(o) =>
                        `${o.variant_name} | Stock: ${o.stock_qty} | ₹${o.sale_price}`
                      }
                      value={(item.variants || []).find((v) => v.variant_id === item.variant) || null}
                      onChange={(e, v) => updateItem(i, "variant", v ? v.variant_id : "")}
                      renderInput={(p) => <TextField {...p} label="Variant" />}
                    />
                  </Grid>
                  <Grid item xs={1}>
                    <TextField
                      label="Qty"
                      type="number"
                      fullWidth
                      value={item.quantity}
                      onChange={(e) => updateItem(i, "quantity", parseInt(e.target.value) || 1)}
                    />
                  </Grid>
                  <Grid item xs={1}>
                    <TextField label="Price" fullWidth value={item.price} disabled />
                  </Grid>
                  <Grid item xs={2}>
                    <TextField
                      label="Discount"
                      type="number"
                      fullWidth
                      value={item.item_discount}
                      onChange={(e) =>
                        updateItem(i, "item_discount", parseFloat(e.target.value) || 0)
                      }
                    />
                  </Grid>
                  <Grid item xs={2}>
                    <TextField
                      label="Total"
                      fullWidth
                      value={Math.max(
                        0,
                        item.quantity * item.price - (item.item_discount || 0)
                      )}
                      disabled
                    />
                  </Grid>


                  <Grid item xs={1}>
                    <Button color="error" onClick={() => removeItem(i)}>
                      <Delete />
                    </Button>
                  </Grid>
                </Grid>
              ))}

            </>
          )}


          <Button
            variant="outlined"
            startIcon={<Add />}
            onClick={addItem}
            sx={{ mt: 1, mb: 3 }}
          >
            Add Item
          </Button>

          {/* Payment + Totals */}
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              p: 2,
              background: "#f0f6ff",
              borderRadius: 2,
            }}
          >
            <FormControl sx={{ width: 250 }}>
              <InputLabel>Payment Mode</InputLabel>
              <Select
                value={paymentMode}
                onChange={(e) => setPaymentMode(e.target.value)}
                label="Payment Mode"
              >
                <MenuItem value="cash">Cash</MenuItem>
                <MenuItem value="online">Online</MenuItem>
                <MenuItem value="card">Card</MenuItem>
                <MenuItem value="upi">UPI</MenuItem>
                <MenuItem value="other">Other</MenuItem>
              </Select>
            </FormControl>
            <Box textAlign="right">
              <Typography>Subtotal: ₹{totalAmount.toFixed(2)}</Typography>

              {/* 🔹 Discount Type Selector */}
              <Box sx={{ display: "flex", gap: 1, alignItems: "center", mt: 1 }}>
                <FormControl sx={{ minWidth: 120 }}>
                  <InputLabel>Discount Type</InputLabel>
                  <Select
                    value={discountType}
                    onChange={(e) => {
                      setDiscountType(e.target.value);
                      setDiscount(""); // reset clean
                    }}
                    label="Discount Type"
                  >
                    <MenuItem value="amount">Amount</MenuItem>
                    <MenuItem value="percent">Percentage</MenuItem>
                  </Select>
                </FormControl>

                <TextField
                  label={discountType === "percent" ? "Discount (%)" : "Discount (₹)"}
                  type="text"
                  value={discount}
                  onChange={(e) => {
                    let val = e.target.value;

                    // ✅ Remove all leading zeros
                    val = val.replace(/^0+(?=\d)/, "");

                    // ✅ Only allow numeric and up to 2 decimals
                    if (/^\d*\.?\d*$/.test(val)) {
                      setDiscount(val);
                    }
                  }}
                  onBlur={() => {
                    // ✅ Clean empty or 0 values
                    if (discount === "" || parseFloat(discount) <= 0) {
                      setDiscount("");
                    }
                  }}
                  sx={{ width: 180 }}
                />
              </Box>

              {/* 🔹 Auto Net Amount Calculation */}
              <Typography
                variant="h6"
                color="success.main"
                sx={{ mt: 1, fontWeight: 700 }}
              >
                {discountType === "percent"
                  ? discount && parseFloat(discount) > 0
                    ? `Net Amount: ₹${(
                      totalAmount -
                      (totalAmount * parseFloat(discount)) / 100
                    ).toFixed(2)}`
                    : `Net Amount: ₹${totalAmount.toFixed(2)}`
                  : discount && parseFloat(discount) > 0
                    ? `Net Amount: ₹${Math.max(
                      0,
                      totalAmount - parseFloat(discount)
                    ).toFixed(2)}`
                    : `Net Amount: ₹${totalAmount.toFixed(2)}`}
              </Typography>
            </Box>

          </Box>


          <Button
            variant="contained"
            color="primary"
            fullWidth
            sx={{ mt: 4, py: 1.5, fontSize: 18 }}
            onClick={handleSubmit}
            disabled={loading}
          >
            {loading ? "Processing..." : "Create Invoice"}
          </Button>
        </CardContent>
      </Card>

      {/* ✅ Success Modal */}
      <Dialog
        open={openModal}
        onClose={() => setOpenModal(false)}
        PaperProps={{
          sx: {
            borderRadius: "16px",
            padding: "10px",
            minWidth: "360px",
          },
        }}
      >
        <DialogTitle
          sx={{
            textAlign: "center",
            pb: 1,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
          }}
        >
          <Box
            sx={{
              backgroundColor: "#E8F5E9",
              borderRadius: "50%",
              width: 80,
              height: 80,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              boxShadow: "0 4px 10px rgba(76,175,80,0.3)",
            }}
          >
            <CheckCircle color="success" sx={{ fontSize: 48 }} />
          </Box>

          <Typography variant="h6" mt={2} sx={{ fontWeight: 700, color: "#2e7d32" }}>
            Invoice Created Successfully
          </Typography>
        </DialogTitle>

        <DialogContent sx={{ textAlign: "center", mt: 1 }}>
          <Typography variant="body1" mt={1}>
            Invoice No: <strong>{invoiceData?.invoice_no}</strong>
          </Typography>
          <Typography variant="body2" mt={1}>
            Net Amount: <strong>₹{invoiceData?.net_amount}</strong>
          </Typography>

          {/* Buttons Row */}
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              gap: 2,
              mt: 3,
              flexWrap: "wrap",
            }}
          >
            {/* 🖨️ Normal Print */}
            <Button
              variant="contained"
              size="small"
              startIcon={<Print />}
              onClick={() => handleInvoicePrint(invoiceData?.invoice_id)}
              sx={{
                borderRadius: "10px",
                fontWeight: 600,
                padding: "8px 18px",
                color: "#fff",
                background: "linear-gradient(90deg, #42a5f5, #1e88e5)",
                boxShadow: "0 4px 10px rgba(33,150,243,0.3)",
                textTransform: "none",
                "&:hover": {
                  transform: "scale(1.05)",
                  boxShadow: "0 8px 16px rgba(33,150,243,0.4)",
                  background: "linear-gradient(90deg, #1e88e5, #1565c0)",
                },
              }}
            >
              Print
            </Button>

            {/* 🧾 Thermal Print */}
            <Button
              variant="contained"
              size="small"
              startIcon={<Print />}
              onClick={() => handleThermalInvoicePrint(invoiceData?.invoice_id)}
              sx={{
                borderRadius: "10px",
                fontWeight: 600,
                padding: "8px 18px",
                color: "#fff",
                background: "linear-gradient(90deg, #43a047, #2e7d32)",
                boxShadow: "0 4px 10px rgba(46,125,50,0.35)",
                textTransform: "none",
                "&:hover": {
                  transform: "scale(1.05)",
                  boxShadow: "0 8px 16px rgba(46,125,50,0.45)",
                  background: "linear-gradient(90deg, #2e7d32, #1b5e20)",
                },
              }}
            >
              Thermal Print
            </Button>

            {/* ❌ Close */}
            <Button
              variant="outlined"
              color="error"
              size="small"
              onClick={() => setOpenModal(false)}
              sx={{
                borderRadius: "10px",
                fontWeight: 600,
                padding: "8px 18px",
                borderColor: "#ef5350",
                color: "#d32f2f",
                textTransform: "none",
                "&:hover": {
                  backgroundColor: "#ffebee",
                  borderColor: "#d32f2f",
                },
              }}
            >
              Close
            </Button>
          </Box>
        </DialogContent>
      </Dialog>

    </Box>
  );
};

export default ProductBilling;
